import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Star, Warning } from "@phosphor-icons/react";

// ============================================================
// Admin: Riassegna pratica → sceglie operatore tra i disponibili
// ============================================================
export function ReassignDialog({ open, onClose, practiceId, onDone }) {
  const [ops, setOps] = useState([]);
  useEffect(() => {
    if (!open || !practiceId) return;
    api.get(`/practices/${practiceId}/available-operators`).then(r => setOps(r.data || []));
  }, [open, practiceId]);

  const assign = async (opId) => {
    try {
      await api.post(`/practices/${practiceId}/reassign?operator_id=${opId}`);
      toast.success("Pratica riassegnata");
      onDone && onDone();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Riassegna pratica</DialogTitle>
          <DialogDescription>Scegli un operatore tra quelli abilitati al servizio.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1 max-h-[60vh] overflow-y-auto">
          {ops.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">Nessun operatore disponibile.</div>
          ) : ops.map(o => (
            <div key={o.operator_id} className={`flex items-center justify-between p-3 border rounded ${!o.available ? "opacity-50" : "hover:border-purple-400"}`}>
              <div className="flex-1">
                <div className="font-medium text-sm">{o.first_name} {o.last_name}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                  <span className="flex items-center gap-0.5"><Star size={12} weight="fill" color="#f59e0b" /> {o.score}/5</span>
                  <span>· {o.active_load} in corso</span>
                </div>
              </div>
              <Button size="sm" disabled={!o.available} onClick={() => assign(o.operator_id)} className="ht-gradient border-0">Assegna</Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// Admin: Annulla pratica con doppia conferma + messaggio email
// ============================================================
export function AdminRevokeDialog({ open, onClose, practiceId, serviceName, onDone }) {
  const [message, setMessage] = useState("");
  const [step, setStep] = useState(1); // 1 = messaggio, 2 = conferma finale
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setMessage("");
      setStep(1);
      setSubmitting(false);
    }
  }, [open]);

  const proceed = () => {
    if (message.trim().length < 5) {
      toast.error("Inserisci un messaggio (min 5 caratteri) da inviare al professionista");
      return;
    }
    setStep(2);
  };

  const confirm = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/practices/${practiceId}/revoke`, { message: message.trim() });
      toast.success(r.data?.refunded ? "Pratica annullata + rimborso Stripe inviato" : "Pratica annullata");
      onDone && onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante l'annullamento");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
      <DialogContent className="max-w-lg" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Warning size={18} weight="fill" color="#B91C1C" />
            {step === 1 ? "Annulla pratica" : "Conferma definitiva"}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? `Stai per annullare "${serviceName || "questa pratica"}". Il servizio resterà nello storico con dicitura ANNULLATO e importo €0. Il plafond del professionista verrà ripristinato; se pagata dal cliente, sarà emesso un rimborso Stripe.`
              : "Questa azione è irreversibile. Confermi di voler procedere?"}
          </DialogDescription>
        </DialogHeader>
        {step === 1 ? (
          <div>
            <label className="text-sm font-medium">Messaggio al professionista (obbligatorio)</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Motivo dell'annullamento…"
              rows={5}
              className="mt-2"
              data-testid="admin-revoke-message"
              autoFocus
            />
            <div className="text-xs text-muted-foreground mt-1">{message.length} caratteri</div>
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <div className="text-sm text-red-900 font-medium mb-2">Messaggio che verrà inviato via email:</div>
            <div className="text-sm text-red-800 whitespace-pre-wrap">{message}</div>
          </div>
        )}
        <DialogFooter>
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={onClose} data-testid="admin-revoke-cancel">Annulla</Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={proceed}
                data-testid="admin-revoke-continue"
              >
                Continua
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)} disabled={submitting} data-testid="admin-revoke-back">Indietro</Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirm}
                disabled={submitting}
                data-testid="admin-revoke-confirm"
              >
                {submitting ? "Annullamento…" : "Revoca definitivamente"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
